import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ImagePaths } from "../../../Utility/ImagePaths";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import "../../../Styles/Configuration.css";

const INPUT_SX = {
  '& .MuiInputBase-root': { height: '2.25vw' },
  '& .MuiInputBase-input': { fontSize: '0.95vw', padding: '0.3vw 0.6vw' },
  '& .MuiSelect-select': { fontSize: '0.95vw', padding: '0.3vw 0.6vw' },
  '& .MuiInputLabel-root': { fontSize: '0.95vw', top: '-0.3vw', marginTop: '0.1vw' },
  '& .MuiInputLabel-shrink': { top: '0.05vw' },
  '& .MuiOutlinedInput-notchedOutline legend': { fontSize: '0.8vw' },
  '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' },
  '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { fontSize: '1.1vw' },
};

// ── Default screen structure ──────────────────────────────────────────────────
const getDefaultScreens = () => [
  {
    title: "Home",
    viewAccess: false,
    editAccess: false,
    main_tabs: [
      { title: "New Bill", viewAccess: false, editAccess: false },
      { title: "Existing Bill", viewAccess: false, editAccess: false },
    ],
  },
  {
    title: "Specimen",
    viewAccess: false,
    editAccess: false,
    main_tabs: [
      { title: "Specimen Collection", viewAccess: false, editAccess: false },
      { title: "Outsource Test", viewAccess: false, editAccess: false },
    ],
  },
  {
    title: "Billing Transaction",
    viewAccess: false,
    editAccess: false,
    main_tabs: [
      { title: "Overall Bills", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "Credit Bills", viewAccess: false, editAccess: false },
      { title: "Receipt Bills", viewAccess: false, editAccess: false, hideEdit: true },
    ],
  },
  {
    title: "Credit Settlement",
    viewAccess: false,
    editAccess: false,
    main_tabs: [
      { title: "Hospital Settlement", viewAccess: false, editAccess: false },
      { title: "Settlement Report", viewAccess: false, editAccess: false, hideEdit: true },
    ],
  },
  {
    title: "Reporting",
    viewAccess: false,
    editAccess: false,
    main_tabs: [
      { title: "Pending List", viewAccess: false, editAccess: false },
      { title: "Approval List", viewAccess: false, editAccess: false },
      { title: "Completed List", viewAccess: false, editAccess: false },
    ],
  },
  {
    title: "Reports",
    viewAccess: false,
    editAccess: false,
    hideEdit: true,
    main_tabs: [
      { title: "Outsource Report", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "TAT Report", viewAccess: false, editAccess: false, hideEdit: true },
    ],
  },
  {
    title: "Masters",
    viewAccess: false,
    editAccess: false,
    hideEdit: true,
    main_tabs: [
      { title: "Service Master", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "Result Template", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "Specimen Master", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "Demographics Layout", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "Outsource Configuration", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "E-Sign Configuration", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "Referred Doctor", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "Custom Configuration", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "Hospital Configuration", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "Role Access", viewAccess: false, editAccess: false, hideEdit: true },
      { title: "User Configuration", viewAccess: false, editAccess: false, hideEdit: true },
    ],
  },
];

// ── Merge saved access into defaults ─────────────────────────────────────────
const mergeScreenAccess = (defaults, saved) => {
  if (!saved?.length) return defaults;
  return defaults.map((screen) => {
    const s = saved.find((x) => x.title === screen.title);
    if (!s) return screen;
    return {
      ...screen,
      viewAccess: s.viewAccess ?? false,
      editAccess: s.editAccess ?? false,
      main_tabs: screen.main_tabs.map((tab) => {
        const t = s.main_tabs?.find((x) => x.title === tab.title);
        if (!t) return tab;
        return {
          ...tab,
          viewAccess: t.viewAccess ?? false,
          editAccess: t.editAccess ?? false,
          sub_tabs: tab.sub_tabs?.map((sub) => {
            const st = t.sub_tabs?.find((x) => x.title === sub.title);
            return st
              ? { ...sub, viewAccess: st.viewAccess ?? false, editAccess: st.editAccess ?? false }
              : sub;
          }),
        };
      }),
    };
  });
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function AddRoleAccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const passedState = location.state || {};

  const [isLoader, setIsLoader] = useState(false);
  const [roleName, setRoleName] = useState(passedState.role_name || "");
  const [roleCode] = useState(passedState.role_code || "");
  const [editId] = useState(passedState.edit_id || null);
  const [screenList, setScreenList] = useState(
    mergeScreenAccess(structuredClone(getDefaultScreens()), passedState.permission_access || passedState.screen_access || [])
  );
  const [message, setMessage] = useState({
    isErrorMsg: false, isErrorMsgText: "",
    successMsg: false, successMsgText: "",
  });

  const errorMessage = (msg) =>
    setMessage((p) => ({ ...p, isErrorMsg: true, isErrorMsgText: msg }));

  const successMessage = (msg) =>
    setMessage((p) => ({ ...p, successMsg: true, successMsgText: msg }));

  const msgClose = () =>
    setMessage({ isErrorMsg: false, isErrorMsgText: "", successMsg: false, successMsgText: "" });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const updateScreen = (screenTitle, updater) =>
    setScreenList((prev) => prev.map((s) => s.title === screenTitle ? updater(s) : s));

  const handleScreenView = (screenTitle, checked) =>
    updateScreen(screenTitle, (s) => ({
      ...s,
      viewAccess: checked,
      editAccess: checked ? s.editAccess : false,
      main_tabs: s.main_tabs.map((t) => ({
        ...t,
        viewAccess: checked,
        editAccess: checked ? t.editAccess : false,
        sub_tabs: t.sub_tabs?.map((st) => ({
          ...st, viewAccess: checked, editAccess: checked ? st.editAccess : false,
        })),
      })),
    }));

  const handleScreenEdit = (screenTitle) =>
    updateScreen(screenTitle, (s) => {
      const tog = !s.editAccess;
      return {
        ...s, editAccess: tog,
        main_tabs: s.main_tabs.map((t) => ({
          ...t, editAccess: t.viewAccess ? tog : false,
          sub_tabs: t.sub_tabs?.map((st) => ({ ...st, editAccess: st.viewAccess ? tog : false })),
        })),
      };
    });

  const handleTabView = (screenTitle, tabTitle, checked) =>
    updateScreen(screenTitle, (s) => {
      const tabs = s.main_tabs.map((t) =>
        t.title !== tabTitle ? t : {
          ...t, viewAccess: checked, editAccess: checked ? t.editAccess : false,
          sub_tabs: t.sub_tabs?.map((st) => ({
            ...st, viewAccess: checked, editAccess: checked ? st.editAccess : false,
          })),
        }
      );
      return {
        ...s, main_tabs: tabs,
        viewAccess: tabs.some((t) => t.viewAccess) ? s.viewAccess : false,
        editAccess: tabs.some((t) => t.viewAccess) ? s.editAccess : false,
      };
    });

  const handleTabEdit = (screenTitle, tabTitle) =>
    updateScreen(screenTitle, (s) => ({
      ...s,
      main_tabs: s.main_tabs.map((t) => {
        if (t.title !== tabTitle) return t;
        const tog = !t.editAccess;
        return {
          ...t, editAccess: tog,
          sub_tabs: t.sub_tabs?.map((st) => ({ ...st, editAccess: st.viewAccess ? tog : false })),
        };
      }),
    }));

  const handleSubView = (screenTitle, tabTitle, subTitle, checked) =>
    updateScreen(screenTitle, (s) => ({
      ...s,
      main_tabs: s.main_tabs.map((t) => {
        if (t.title !== tabTitle) return t;
        const subs = (t.sub_tabs || []).map((st) =>
          st.title === subTitle
            ? { ...st, viewAccess: checked, editAccess: checked ? st.editAccess : false }
            : st
        );
        return {
          ...t, sub_tabs: subs,
          viewAccess: subs.some((st) => st.viewAccess) ? t.viewAccess : false,
          editAccess: subs.some((st) => st.viewAccess) ? t.editAccess : false,
        };
      }),
    }));

  const handleSubEdit = (screenTitle, tabTitle, subTitle) =>
    updateScreen(screenTitle, (s) => ({
      ...s,
      main_tabs: s.main_tabs.map((t) =>
        t.title !== tabTitle ? t : {
          ...t,
          sub_tabs: (t.sub_tabs || []).map((st) =>
            st.title === subTitle ? { ...st, editAccess: !st.editAccess } : st
          ),
        }
      ),
    }));

  // ── Save / Reset ──────────────────────────────────────────────────────────
  const handleSave = () => {
    if (!roleName.trim()) { errorMessage("Enter Role Name"); return; }
    if (!screenList.some((s) => s.viewAccess)) {
      errorMessage("Give access for at least one screen"); return;
    }
    const payload = {
      role_name: roleName.trim(),
      permission_access: screenList,
      ...(editId ? { id: editId } : {}),
    };
    setIsLoader(true);
    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_ROLE_MASTER)
      .then((res) => {
        if (res?.data?.status === "success") {
          successMessage(res?.data?.message || "Saved successfully");
          setTimeout(() => navigate("/HistopathologySetting/UserConfiguration/RoleAccess"), 1500);
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  };

  const handleReset = () => {
    setRoleName("");
    setScreenList(structuredClone(getDefaultScreens()));
  };

  // ── Reusable row renderer ─────────────────────────────────────────────────
  const renderRow = (label, fontWeight, indent, viewChecked, viewDisabled, onView, editAccess, editDisabled, onEdit, hideEdit = false) => (
    <Box display="flex" alignItems="center" className="eMed_userAccessRow" pl={indent}>
      <Box flex={0.7} display="flex" alignItems="center">
        <FormControlLabel
          label={<Typography fontWeight={fontWeight} fontSize="0.9dvw">{label}</Typography>}
          control={
            <Checkbox size="small" checked={viewChecked} disabled={viewDisabled} onChange={onView} />
          }
        />
      </Box>
      <Box flex={0.3} display="flex" alignItems="center" justifyContent="flex-end" pr="3vw">
        {!hideEdit && (
          <IconButton size="small" disabled={editDisabled} onClick={onEdit}>
            <img
              src={editAccess ? ImagePaths.UserEditOn?.default ?? ImagePaths.UserEditOn : ImagePaths.UserEditOff?.default ?? ImagePaths.UserEditOff}
              className="eMed_list_image" alt="edit"
            />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  // ── Access tree (full, all screens) ──────────────────────────────────────
  const renderAccessTree = () => (
    <Box className="eMed_mod_card">
      {screenList.map((screen, si) => (
        <Box key={si} sx={{ display: "flex", flexDirection: "column" }}>

          {/* Screen row */}
          {renderRow(
            screen.title, 700, "0.5vw",
            screen.viewAccess, false,
            (e) => handleScreenView(screen.title, e.target.checked),
            screen.editAccess, !screen.viewAccess,
            () => handleScreenEdit(screen.title),
            screen.hideEdit
          )}

          {/* Main tab rows */}
          {screen.main_tabs.map((tab, ti) => (
            <Box key={ti} sx={{ display: "flex", flexDirection: "column" }}>
              {renderRow(
                tab.title, 600, "2.5vw",
                tab.viewAccess, !screen.viewAccess,
                (e) => handleTabView(screen.title, tab.title, e.target.checked),
                tab.editAccess, !tab.viewAccess,
                () => handleTabEdit(screen.title, tab.title),
                tab.hideEdit
              )}

              {/* Sub-tab rows */}
              {tab.sub_tabs?.map((sub, sti) =>
                renderRow(
                  sub.title, 400, "4.5vw",
                  sub.viewAccess, !tab.viewAccess,
                  (e) => handleSubView(screen.title, tab.title, sub.title, e.target.checked),
                  sub.editAccess, !sub.viewAccess,
                  () => handleSubEdit(screen.title, tab.title, sub.title),
                  sub.hideEdit
                )
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", backgroundColor: "white", height: "80vh", display: "flex", flexDirection: "column", p: "3px", boxSizing: "border-box" }}>

      {/* Body */}
      <Grid container sx={{ flex: 1, overflow: "hidden" }}>

        {/* Left panel — Role fields */}
        <Grid item xs={12} md={3}>
          <Box sx={{ borderRight: "1px solid #e0e0e0", height: "100%", p: "1vw" }}>
            <Typography variant="subtitle2" fontWeight={700} mb={2}>
              Role Details
            </Typography>
            <TextField
              fullWidth
              disabled
              label="Role Code"
              value={roleCode}
              size="small"
              sx={{ mb: 2, ...INPUT_SX }}
            />
            <TextField
              fullWidth
              label="Role Name *"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              inputProps={{ maxLength: 100 }}
              size="small"
              sx={{ ...INPUT_SX }}
            />
          </Box>
        </Grid>

        {/* Right panel — Screen Access */}
        <Grid item xs={12} md={9}>
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Column headers */}
            <Box display="flex" alignItems="center"
              sx={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #ddd", px: "0.5vw", py: "0.4vw" }}>
              <Box flex={0.7}>
                <Typography fontWeight={700} fontSize="0.85dvw" pl="0.3vw">View Access</Typography>
              </Box>
              <Box flex={0.3} display="flex" justifyContent="flex-end" pr="3vw">
                <Typography fontWeight={700} fontSize="0.85dvw">Edit Access</Typography>
              </Box>
            </Box>
            {/* Tree */}
            <Box sx={{ flex: 1, overflowY: "auto", px: "0.5vw", pt: "0.5vw" }}>
              {renderAccessTree()}
            </Box>
          </Box>
        </Grid>

      </Grid>

      {/* Footer */}
      <Box display="flex" justifyContent="flex-end" gap={1} p="0.5vw"
        sx={{ borderTop: "1px solid #e8e8e8" }}>
        <Button size="small" variant="outlined" sx={{ textTransform: "capitalize" }} onClick={handleReset}>
          Reset
        </Button>
        <Button size="small" variant="contained" sx={{ textTransform: "capitalize" }} onClick={handleSave}>
          {editId ? "Update Role" : "Add Role"}
        </Button>
      </Box>

      {message.isErrorMsg && <ToastMsg severity="error" msg={message.isErrorMsgText} msgPop={msgClose} />}
      {message.successMsg && <ToastMsg severity="success" msg={message.successMsgText} msgPop={msgClose} />}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
